// src/storage.ts — Local storage with localforage

import localforage from 'localforage';
import { AppState, PRESET_CHALLENGES } from './types';

const STORE_KEY = 'hardtrack-app-state';
const PHOTOS_STORE = 'hardtrack-photos';

localforage.config({
  name: '75HardTracker',
  storeName: 'app_data',
});

const photosStore = localforage.createInstance({
  name: '75HardTracker',
  storeName: 'photos',
});

export const defaultState: AppState = {
  challenges: [...PRESET_CHALLENGES],
  attempts: [],
  activeAttemptId: null,
  onboarded: false,
  premium: false,
  settings: {
    theme: 'dark',
  },
};

export async function loadState(): Promise<AppState> {
  try {
    const data = await localforage.getItem<AppState>(STORE_KEY);
    if (data) {
      return {
        ...defaultState,
        ...data,
        challenges: data.challenges?.length
          ? data.challenges
          : defaultState.challenges,
        settings: { ...defaultState.settings, ...data.settings },
      };
    }
    return defaultState;
  } catch {
    return defaultState;
  }
}

export async function saveState(state: AppState): Promise<void> {
  await localforage.setItem(STORE_KEY, state);
}

// Photo storage (separate to avoid bloating main state)
export async function savePhoto(attemptId: string, date: string, dataUri: string): Promise<void> {
  const key = `${attemptId}:${date}`;
  await photosStore.setItem(key, dataUri);
}

export async function getPhoto(attemptId: string, date: string): Promise<string | null> {
  const key = `${attemptId}:${date}`;
  return photosStore.getItem<string>(key);
}

export async function getAllPhotos(attemptId: string): Promise<{ date: string; dataUri: string }[]> {
  const photos: { date: string; dataUri: string }[] = [];
  const prefix = `${attemptId}:`;
  await photosStore.iterate<string, void>((value, key) => {
    if (key.startsWith(prefix)) {
      photos.push({ date: key.slice(prefix.length), dataUri: value });
    }
  });
  photos.sort((a, b) => a.date.localeCompare(b.date));
  return photos;
}

export async function exportData(): Promise<string> {
  const state = await loadState();
  return JSON.stringify(state, null, 2);
}
